import {Injectable} from '@angular/core';
import {BundleTemplate, CanonicalId, DcpBundleTemplate, IProduct, TypeValuePair} from '../models';
import {JsonService} from './json.service';

const CANONICAL_ID: string = 'Canonical_ID';

@Injectable({
  providedIn: 'root',
})
export abstract class ProductUtilitiesService {
  protected constructor(protected jsonService: JsonService) {}

  abstract getPermutationProductId(cId: CanonicalId): string;

  /**
   * Returns the product Canonical id.
   *
   * @param product product to extract the Canonical id of.
   * @param bundleTemplate Type of the offer. Determines which Canonical is read from the product data.
   * If left blank the display product code will be returned.
   */
  getCanonicalId(product: IProduct, bundleTemplate?: BundleTemplate): CanonicalId {
    if (product?.data || bundleTemplate?.data) {
      const fetchedCanonicalId: TypeValuePair = this.findCanonicalIdDataSet(product, bundleTemplate);
      if (fetchedCanonicalId && fetchedCanonicalId.value) {
        try {
          return this.jsonService.parseSafely(fetchedCanonicalId.value) as CanonicalId;
        } catch (e) {}
      }
    }
    return {productId: {id: '', catalog: '', version: ''}};
  }

  /**
   * Searches in  product or bundleTemplate for the CANONICAL_ID.
   *
   * @param product current product.
   * @param bundleTemplate selected bundleTemplate. Optional => if not present, the CustomJsonData of product is returned.
   */
  findCanonicalIdDataSet(product: IProduct, bundleTemplate?: BundleTemplate): TypeValuePair {
    let canonicalContainer: any;
    if (bundleTemplate) {
      canonicalContainer = bundleTemplate;
    }
    if (!canonicalContainer) {
      canonicalContainer = product;
    }
    return canonicalContainer.data.find(
      (entry: TypeValuePair) => entry.type.toLowerCase() === CANONICAL_ID.toLowerCase(),
    );
  }

  /**
   * Returns the product code. eg: display-a0c6139f-23f2-44df-a6f1-ef5e8d33d0b3_live_v1
   *
   * @param product product to get the code of.
   * @param bundleTemplate Type of the offer. Determines which Canonical is read from the product data.
   * If left blank the display product code will be returned.
   */
  getProductCode(product: IProduct, bundleTemplate?: BundleTemplate): string | undefined {
    if (bundleTemplate) {
      const canonicalId: CanonicalId = this.getCanonicalId(product, bundleTemplate);
      return this.getPermutationProductId(canonicalId) || product.code;
    }
    return product.code;
  }

  /**
   * Fetches a bundle template by alternative ID for a given product.
   *
   * @param {Product} product - The product object for which to fetch the bundle template.
   * @param {string} supplierAID - The alternative ID of the supplier.
   *
   * @returns {DcpBundleTemplate} - The bundle template with the matching alternative ID, or undefined if not found.
   */
  fetchBundleTemplateByAlternativeId(product: IProduct, supplierAID: string): DcpBundleTemplate | undefined {
    return product.bundleTemplates?.find(
      (bundleTemplate: DcpBundleTemplate) => bundleTemplate.supplierAlternativeAID == supplierAID,
    );
  }

  getProductTechnicalName(product: IProduct, bundleMaterialCode: string): string {
    const canonicalId: CanonicalId = this.getCanonicalId(
      product,
      product?.bundleTemplates?.find(
        (bundleTemplate: BundleTemplate) => bundleTemplate.supplierAlternativeAID === bundleMaterialCode,
      ),
    );
    return canonicalId.productId.id;
  }
}
